// src/components/navigation/MainNavbar.tsx
// DROP 24 — Mega-Menu Navigation (replaces flat icon-tab scheme)
// All 34 tabs accessible via grouped dropdown menus
// Mobile: hamburger accordion | Desktop: hover/click dropdowns

import React, { useState, useEffect, useRef, useCallback } from "react";
import { PortalTab } from "../../types";
import {
  Menu, X, ChevronDown, LogIn, LogOut, User,
  Home, Calendar, Image, Radio, FileText, Clock,
  Crown, Users, Shield, Landmark,
  Ticket, Heart, ShoppingBag, UserPlus, Vote,
  Newspaper, Map, Globe, HelpCircle, Phone,
  Settings, LayoutDashboard, Store,
  Briefcase, Award
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

interface CurrentUser {
  email: string;
  name?: string;
  systemRole?: string;
  permissions?: string[];
}

interface Props {
  currentUser: CurrentUser | null;
  activeTab: PortalTab;
  setActiveTab: (tab: PortalTab) => void;
  onLoginClick: () => void;
  onLogout: () => void;
}

// ─── Public Mega-Menu Groups ──────────────────────────────────────────────────

interface NavItem {
  id: PortalTab;
  label: string;
  icon: React.ReactNode;
  description: string;
}

interface NavGroup {
  key: string;
  label: string;
  emoji: string;
  items: NavItem[];
}

const PUBLIC_MENU_GROUPS: NavGroup[] = [
  {
    key: "festival",
    label: "Festival",
    emoji: "🎉",
    items: [
      { id: "festival-home",  label: "Home",           icon: <Home size={15}/>,     description: "Festival homepage & countdown" },
      { id: "programme",      label: "Programme",      icon: <FileText size={15}/>, description: "Full Dec 24–30 programme" },
      { id: "schedule",       label: "Schedule",       icon: <Clock size={15}/>,    description: "Timetable & safety info" },
      { id: "calendar",       label: "Calendar",       icon: <Calendar size={15}/>, description: "Events calendar" },
      { id: "gallery",        label: "Gallery",        icon: <Image size={15}/>,    description: "Photos & videos" },
      { id: "live-stream",    label: "Live Stream",    icon: <Radio size={15}/>,    description: "Watch ERUE live" },
    ],
  },
  {
    key: "kingdom",
    label: "Kingdom",
    emoji: "👑",
    items: [
      { id: "ogieneni",          label: "Royal Palace",      icon: <Crown size={15}/>,       description: "HRH The Ogieneni of Uzairue" },
      { id: "dignitaries",       label: "Dignitaries",       icon: <Landmark size={15}/>,    description: "Distinguished guests & leaders" },
      { id: "communities",       label: "22 Villages",       icon: <Users size={15}/>,       description: "The 22 communities of Uzairue Kingdom" },
        { id: "heritage",            label: "Cultural Heritage",  icon: <span style={{fontSize:14}}>🏺</span>, description: "Uzairue cultural heritage & traditions" },
        { id: "uzairue-portal",      label: "Uzairue Kingdom",    icon: <span style={{fontSize:14}}>👑</span>, description: "Uzairue Kingdom portal" },
        { id: "management-committee",label: "Mgt Committee",      icon: <span style={{fontSize:14}}>🏛️</span>, description: "Festival Management Committee" },
    ],
  },
  {
    key: "administration",
    label: "Administration",
    emoji: "🏛️",
    items: [
      { id: "board-of-trustees", label: "Board of Trustees", icon: <Award size={15}/>,       description: "ERUE Festival Board of Trustees — oversight & approval" },
      { id: "cwc",               label: "CWC",               icon: <Shield size={15}/>,      description: "Central Working Committee — financial & operational management" },
      { id: "financial-hub", label: "Financial Hub", icon: <span>💰</span>, description: "Accounting, Journals, Budgets, Minutes" },
      { id: "contacts-directory", label: "Contacts Directory", icon: <span style={{fontSize:14}}>📋</span>, description: "CRM — BOT, CWC, LOC, Diaspora, Vendors, Sponsors" },
      { id: "loc",               label: "LOC",               icon: <Briefcase size={15}/>,   description: "Local Organizing Committees — event planning & execution" },
    ],
  },
  {
    key: "participate",
    label: "Participate",
    emoji: "🎯",
    items: [
      { id: "registrations",  label: "Register",       icon: <UserPlus size={15}/>,  description: "Register for festival events" },
      { id: "voting",         label: "Vote",           icon: <Vote size={15}/>,      description: "Vote for Miss ERUE contestants" },
      { id: "raffle",         label: "ERUE Raffle",    icon: <Ticket size={15}/>,    description: "ERUEWin raffle tickets" },
        { id: "collaboration",   label: "Collaborate",     icon: <span style={{fontSize:14}}>🤝</span>, description: "Online meetings, rooms & collaboration" },
      { id: "donate",         label: "Donate",         icon: <Heart size={15}/>,     description: "Support the festival" },
      { id: "vendors",        label: "Vendors",        icon: <Store size={15}/>,     description: "Vendor registration" },
      { id: "store",          label: "Festival Store", icon: <ShoppingBag size={15}/>,description: "Souvenirs & merchandise" },
    ],
  },
  {
    key: "information",
    label: "Information",
    emoji: "📋",
    items: [
      { id: "news",           label: "News Portal",    icon: <Newspaper size={15}/>, description: "Latest ERUE festival news" },
      { id: "visitor-guide",  label: "Visitor Guide",  icon: <Map size={15}/>,       description: "Travel & accommodation info" },
      { id: "diaspora-portal",label: "Diaspora Portal",icon: <Globe size={15}/>,     description: "Uzairue diaspora community" },
      { id: "sponsorship",    label: "Support & Sponsor",icon: <HelpCircle size={15}/>,description: "Sponsorship opportunities" },
      { id: "contact",        label: "Contact Us",     icon: <Phone size={15}/>,     description: "Get in touch with the team" },
    ],
  },
];

// ─── Staff Tabs (RBAC-filtered) ───────────────────────────────────────────────

const STAFF_TABS: {
  id: PortalTab;
  label: string;
  group: string;
  roles: string[];
  perms: string[];
}[] = [
  { id: "governance-admin",   label: "Governance Admin",    group: "Secretariat",  roles: ["PLATFORM_ADMIN","SECRETARY"],                    perms: ["documents"] },
  { id: "board-of-trustees",  label: "Board of Trustees",   group: "Administration", roles: ["PLATFORM_ADMIN","BOARD_TRUSTEE","BOARD_CHAIR"],       perms: ["view_board"] },
  { id: "cwc",                label: "CWC Portal",          group: "Administration", roles: ["PLATFORM_ADMIN","CWC_MEMBER","CWC_TREASURER","CWC_SECRETARY"], perms: ["view_cwc"] },
  { id: "loc",                label: "LOC Portal",          group: "Administration", roles: ["PLATFORM_ADMIN","LOC_CHAIR","LOC_MEMBER"],            perms: ["view_loc"] },
  { id: "document-manager",   label: "Document Manager",    group: "Festival",     roles: ["PLATFORM_ADMIN","SECRETARY","FESTIVAL_COORDINATOR","MEDIA_MANAGER"], perms: ["documents"] },
  { id: "schedule",           label: "Programme Builder",   group: "Festival",     roles: ["PLATFORM_ADMIN","FESTIVAL_COORDINATOR"],          perms: ["programme"] },
  { id: "accounting",         label: "Accounting",          group: "Administration", roles: ["PLATFORM_ADMIN","ACCOUNTANT","CWC_TREASURER"],  perms: ["accounting_edit"] },
  { id: "financial-manager",  label: "Financial Manager",   group: "Administration", roles: ["PLATFORM_ADMIN","ACCOUNTANT","CWC_TREASURER"],  perms: ["finance_manage"] },
  { id: "raffle-admin",       label: "Raffle Admin",        group: "Administration", roles: ["PLATFORM_ADMIN","ACCOUNTANT","RAFFLE_MANAGER","CWC_TREASURER"], perms: ["raffle_manage"] },
  { id: "reports",            label: "Reports Module",      group: "Administration", roles: ["PLATFORM_ADMIN","ACCOUNTANT","FESTIVAL_COORDINATOR","CWC_TREASURER","BOARD_TRUSTEE","BOARD_CHAIR"], perms: ["reports_view"] },
  { id: "community-admin",    label: "Community Admin",     group: "Communities",  roles: ["PLATFORM_ADMIN","COMMUNITY_MANAGER"],             perms: ["community_admin"] },
  { id: "vendor-admin",       label: "Vendor Admin",        group: "Operations",   roles: ["PLATFORM_ADMIN","FESTIVAL_COORDINATOR"],          perms: ["registrations"] },
  { id: "publishing-suite",   label: "News Publishing",     group: "Administration", roles: ["PLATFORM_ADMIN","SECRETARY","FESTIVAL_COORDINATOR","MEDIA_MANAGER"], perms: ["news_publishing"] },
  { id: "communication-portal",label: "Messaging",          group: "Secretariat",  roles: ["PLATFORM_ADMIN","SECRETARY","FESTIVAL_COORDINATOR"], perms: ["communications"] },
  { id: "resources-investment",label: "Resources & Minerals",group: "Operations",  roles: ["PLATFORM_ADMIN"],                                 perms: [] },
  { id: "collaboration-admin", label: "Collaboration Admin", group: "Operations",   roles: ["PLATFORM_ADMIN","SECRETARY","FESTIVAL_COORDINATOR"], icon: "🤝", description: "Manage collaboration rooms and access" },
  { id: "fundraising-admin",   label: "Fundraising Admin",   group: "Operations",   roles: ["PLATFORM_ADMIN","ACCOUNTANT","FESTIVAL_COORDINATOR"], icon: "💝", description: "Manage fundraising campaigns" },
  { id: "village-admin",       label: "Village Admin",       group: "Communities",  roles: ["PLATFORM_ADMIN","COMMUNITY_MANAGER"],                icon: "🏘️", description: "Village portal admin access" },
];

const UNIQUE_STAFF_TABS = STAFF_TABS.filter(
  (tab, idx, arr) => arr.findIndex(t => t.id === tab.id) === idx
);

function canSee(user: CurrentUser, tab: typeof STAFF_TABS[0]): boolean {
  if (!user) return false;
  if (user.systemRole === "PLATFORM_ADMIN" || user.role === "admin") return true;
  if (tab.roles.includes(user.systemRole || user.role?.toUpperCase() || "")) return true;
  if (user.permissions) {
    return tab.perms.some(p => (user.permissions || []).includes(p));
  }
  return false;
}

// ─── Style constants ──────────────────────────────────────────────────────────

const C = {
  bg:         "#070D0A",
  bgSurface:  "#0E1812",
  bgCard:     "#121E16",
  border:     "#1C2E22",
  gold:       "#C9A84C",
  goldLight:  "#E2C97E",
  text:       "#EDE8DC",
  textMuted:  "#8A9E8E",
  green:      "#0F5132",
  dropBg:     "#0A130D",
  dropBorder: "#1C2E22",
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function MainNavbar({
  currentUser,
  activeTab,
  setActiveTab,
  onLoginClick,
  onLogout,
}: Props) {
  const [openGroup,   setOpenGroup]   = useState<string | null>(null);
  const [staffOpen,   setStaffOpen]   = useState(false);
  const [mobileOpen,  setMobileOpen]  = useState(false);
  const [mobileGroup, setMobileGroup] = useState<string | null>(null);
  const [mobileStaff, setMobileStaff] = useState(false);
  const [isDesktop,   setIsDesktop]   = useState(() => typeof window !== "undefined" && window.innerWidth >= 1024);
  const [scrolled,    setScrolled]    = useState(false);
  const navRef = useRef<HTMLDivElement>(null);

  // Scroll shadow
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenGroup(null);
        setStaffOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close mobile on resize → desktop
  useEffect(() => {
    const onResize = () => {
      const desktop = window.innerWidth >= 1024;
      setIsDesktop(desktop);
      if (desktop) setMobileOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleLogout = useCallback(() => {
    setOpenGroup(null);
    setStaffOpen(false);
    setMobileOpen(false);
    onLogout();
  }, [onLogout]);

  const navigate = useCallback((tab: PortalTab) => {
    setActiveTab(tab);
    setOpenGroup(null);
    setStaffOpen(false);
    setMobileOpen(false);
    setMobileGroup(null);
    setMobileStaff(false);
  }, [setActiveTab]);

  const toggleGroup = useCallback((key: string) => {
    setOpenGroup(prev => prev === key ? null : key);
    setStaffOpen(false);
  }, []);

  const toggleStaff = useCallback(() => {
    setStaffOpen(prev => !prev);
    setOpenGroup(null);
  }, []);

  // ── RBAC ──────────────────────────────────────────────────────────────────

  const permitted = currentUser
    ? UNIQUE_STAFF_TABS.filter(t => canSee(currentUser, t))
    : [];

  const grouped = permitted.reduce((acc, tab) => {
    if (!acc[tab.group]) acc[tab.group] = [];
    acc[tab.group].push(tab);
    return acc;
  }, {} as Record<string, typeof UNIQUE_STAFF_TABS>);

  const groupOrder = ["Secretariat", "Festival", "Administration", "Communities", "Operations"];

  // ── Cloudinary logo ───────────────────────────────────────────────────────

  const LOGO_URL = "https://res.cloudinary.com/uuovmrek/image/upload/e_bgremoval/uzairuelogo01";

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div
      ref={navRef}
      style={{
        position:  "sticky",
        top:       0,
        zIndex:    1000,
        background: C.bg,
        boxShadow: scrolled ? `0 2px 20px rgba(0,0,0,0.6)` : "none",
        borderBottom: `1px solid ${C.border}`,
        transition: "box-shadow 0.2s",
      }}
    >
      {/* ── TIER 1: Info bar ──────────────────────────────────────────────── */}
      <div style={{
        background:    C.bgSurface,
        borderBottom:  `1px solid ${C.border}`,
        padding:       "5px 24px",
        display:       "flex",
        alignItems:    "center",
        justifyContent:"space-between",
        flexWrap:      "wrap" as const,
        gap:           8,
        fontSize:      11,
        color:         C.textMuted,
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:14, flexWrap:"wrap" as const }}>
          <span>📞 +234 705 7840 320</span>
          <span style={{ opacity:0.3 }}>|</span>
          <span>✉️ erueuzairue@gmail.com</span>
          <span style={{ opacity:0.3 }}>|</span>
          <span>📍 Jattu-Uzairue, Etsako West, Edo State</span>
        </div>
        <span style={{ color:C.gold, fontSize:10, letterSpacing:2, textTransform:"uppercase" as const, fontWeight:600 }}>
          ERUE Festival 2026 · Dec 24–30
        </span>
      </div>

      {/* ── TIER 2: Main nav bar ──────────────────────────────────────────── */}
      <div style={{
        display:        "flex",
        alignItems:     "center",
        justifyContent: "space-between",
        padding:        "0 20px",
        height:         62,
        gap:            8,
      }}>

        {/* Logo */}
        <button
          onClick={() => navigate("festival-home")}
          style={{
            display:    "flex",
            alignItems: "center",
            gap:        10,
            background: "none",
            border:     "none",
            cursor:     "pointer",
            flexShrink: 0,
            padding:    0,
          }}
          aria-label="ERUE Festival Home"
        >
          <div style={{
            width:         40,
            height:        40,
            borderRadius:  "50%",
            background:    C.bg,
            overflow:      "hidden",
            display:       "flex",
            alignItems:    "center",
            justifyContent:"center",
            border:        `1px solid ${C.border}`,
          }}>
            <img
              src={LOGO_URL}
              alt="Uzairue Logo"
              style={{ width:36, height:36, objectFit:"contain", mixBlendMode:"screen" }}
              onError={e => { (e.target as HTMLImageElement).src =
                "https://res.cloudinary.com/uuovmrek/image/upload/uzairuelogo01"; }}
            />
          </div>
          <div style={{ textAlign:"left" as const }}>
            <div style={{
              fontFamily: "Cinzel, serif",
              fontSize:   13,
              fontWeight: 700,
              color:      C.gold,
              lineHeight: 1.2,
              letterSpacing: 0.5,
            }}>ERUE Festival</div>
            <div style={{ fontSize:9, color:C.textMuted, letterSpacing:1.5, textTransform:"uppercase" as const }}>
              Uzairue Kingdom
            </div>
          </div>
        </button>

        {/* ── Desktop mega-menu ──────────────────────────────────────────── */}
        <nav
          style={{ display: isDesktop ? "flex" : "none", alignItems:"center", gap:2, flex:1, justifyContent:"center" }}
          aria-label="Main navigation"
        >
          {PUBLIC_MENU_GROUPS.map(group => (
            <div key={group.key} style={{ position:"relative" }}>
              <button
                onClick={() => toggleGroup(group.key)}
                style={{
                  display:     "flex",
                  alignItems:  "center",
                  gap:         5,
                  padding:     "8px 12px",
                  background:  openGroup === group.key ? C.bgCard : "none",
                  border:      "none",
                  borderRadius: 6,
                  cursor:      "pointer",
                  color:       openGroup === group.key ? C.gold : C.text,
                  fontSize:    13,
                  fontWeight:  500,
                  transition:  "all 0.15s",
                  whiteSpace:  "nowrap" as const,
                }}
                aria-expanded={openGroup === group.key}
                aria-haspopup="true"
              >
                <span>{group.emoji}</span>
                <span>{group.label}</span>
                <ChevronDown
                  size={13}
                  style={{
                    transform:  openGroup === group.key ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s",
                    color:      C.textMuted,
                  }}
                />
              </button>

              {/* Dropdown panel */}
              {openGroup === group.key && (
                <div
                  style={{
                    position:     "absolute",
                    top:          "calc(100% + 6px)",
                    left:         "50%",
                    transform:    "translateX(-50%)",
                    background:   C.dropBg,
                    border:       `1px solid ${C.dropBorder}`,
                    borderRadius: 10,
                    minWidth:     240,
                    boxShadow:    "0 16px 40px rgba(0,0,0,0.6)",
                    zIndex:       200,
                    overflow:     "hidden",
                    animation:    "fadeInDown 0.15s ease",
                  }}
                  role="menu"
                >
                  <div style={{ padding:"6px 0" }}>
                    {group.items.map(item => (
                      <button
                        key={item.id}
                        role="menuitem"
                        onClick={() => navigate(item.id)}
                        style={{
                          display:     "flex",
                          alignItems:  "center",
                          gap:         10,
                          width:       "100%",
                          padding:     "9px 16px",
                          background:  activeTab === item.id ? C.green : "none",
                          border:      "none",
                          cursor:      "pointer",
                          textAlign:   "left" as const,
                          color:       activeTab === item.id ? C.goldLight : C.text,
                          fontSize:    13,
                          transition:  "background 0.12s",
                        }}
                        onMouseEnter={e => {
                          if (activeTab !== item.id)
                            (e.currentTarget as HTMLButtonElement).style.background = C.bgCard;
                        }}
                        onMouseLeave={e => {
                          if (activeTab !== item.id)
                            (e.currentTarget as HTMLButtonElement).style.background = "none";
                        }}
                      >
                        <span style={{ color: activeTab === item.id ? C.gold : C.textMuted, flexShrink:0 }}>
                          {item.icon}
                        </span>
                        <div>
                          <div style={{ fontWeight:500, lineHeight:1.3 }}>{item.label}</div>
                          <div style={{ fontSize:10, color:C.textMuted, lineHeight:1.3 }}>{item.description}</div>
                        </div>
                        {activeTab === item.id && (
                          <div style={{ marginLeft:"auto", width:4, height:4, borderRadius:"50%", background:C.gold }} />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Staff Portal dropdown (logged in only) */}
          {currentUser && permitted.length > 0 && (
            <div style={{ position:"relative" }}>
              <button
                onClick={toggleStaff}
                style={{
                  display:    "flex",
                  alignItems: "center",
                  gap:        5,
                  padding:    "8px 12px",
                  background: staffOpen ? "#1a2e1a" : "none",
                  border:     `1px solid ${staffOpen ? C.green : "transparent"}`,
                  borderRadius: 6,
                  cursor:     "pointer",
                  color:      C.gold,
                  fontSize:   13,
                  fontWeight: 600,
                  whiteSpace: "nowrap" as const,
                }}
                aria-expanded={staffOpen}
              >
                <Shield size={14} />
                <span>Staff Portal</span>
                <ChevronDown size={13} style={{ transform: staffOpen ? "rotate(180deg)":"rotate(0deg)", transition:"transform 0.2s" }} />
              </button>

              {staffOpen && (
                <div style={{
                  position:     "absolute",
                  top:          "calc(100% + 6px)",
                  right:        0,
                  background:   C.dropBg,
                  border:       `1px solid ${C.green}`,
                  borderRadius: 10,
                  minWidth:     220,
                  boxShadow:    "0 16px 40px rgba(0,0,0,0.7)",
                  zIndex:       200,
                  overflow:     "hidden",
                }}>
                  {/* User header */}
                  <div style={{
                    padding:      "12px 16px",
                    borderBottom: `1px solid ${C.border}`,
                    background:   C.bgCard,
                  }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <div style={{
                        width:32, height:32, borderRadius:"50%",
                        background: C.green,
                        display:"flex", alignItems:"center", justifyContent:"center",
                      }}>
                        <User size={16} color={C.gold} />
                      </div>
                      <div>
                        <div style={{ fontSize:12, fontWeight:600, color:C.text }}>
                          {currentUser.name || currentUser.email.split("@")[0]}
                        </div>
                        <div style={{ fontSize:10, color:C.gold, textTransform:"uppercase" as const, letterSpacing:1 }}>
                          {currentUser.systemRole || "Staff"}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Grouped staff tabs */}
                  <div style={{ padding:"4px 0", maxHeight:360, overflowY:"auto" as const }}>
                    {groupOrder.filter(g => grouped[g]?.length).map(groupName => (
                      <div key={groupName}>
                        <div style={{
                          padding:       "6px 16px 2px",
                          fontSize:      9,
                          fontWeight:    700,
                          letterSpacing: 1.5,
                          textTransform: "uppercase" as const,
                          color:         C.textMuted,
                        }}>
                          {groupName}
                        </div>
                        {grouped[groupName].map(tab => (
                          <button
                            key={tab.id}
                            onClick={() => navigate(tab.id)}
                            style={{
                              display:    "flex",
                              alignItems: "center",
                              gap:        8,
                              width:      "100%",
                              padding:    "8px 16px",
                              background: activeTab === tab.id ? C.green : "none",
                              border:     "none",
                              cursor:     "pointer",
                              textAlign:  "left" as const,
                              color:      activeTab === tab.id ? C.goldLight : C.text,
                              fontSize:   12,
                            }}
                            onMouseEnter={e => {
                              if (activeTab !== tab.id)
                                (e.currentTarget as HTMLButtonElement).style.background = C.bgCard;
                            }}
                            onMouseLeave={e => {
                              if (activeTab !== tab.id)
                                (e.currentTarget as HTMLButtonElement).style.background = "none";
                            }}
                          >
                            <Settings size={12} style={{ color: C.textMuted }} />
                            {tab.label}
                          </button>
                        ))}
                      </div>
                    ))}

                    {/* Platform Admin */}
                    {(currentUser.systemRole === "PLATFORM_ADMIN" || currentUser.role === "admin") && (
                      <div>
                        <div style={{ height:1, background:C.border, margin:"6px 0" }} />
                        <button
                          onClick={() => navigate("platform-admin")}
                          style={{
                            display:    "flex",
                            alignItems: "center",
                            gap:        8,
                            width:      "100%",
                            padding:    "8px 16px",
                            background: "none",
                            border:     "none",
                            cursor:     "pointer",
                            color:      C.gold,
                            fontSize:   12,
                            fontWeight: 600,
                          }}
                        >
                          <LayoutDashboard size={13} />
                          Platform Admin Console
                        </button>
                      </div>
                    )}

                    {/* Sign Out */}
                    <div style={{ height:1, background:C.border, margin:"4px 0" }} />
                    <button
                      onClick={handleLogout}
                      style={{
                        display:    "flex",
                        alignItems: "center",
                        gap:        8,
                        width:      "100%",
                        padding:    "8px 16px",
                        background: "none",
                        border:     "none",
                        cursor:     "pointer",
                        color:      "#ef4444",
                        fontSize:   12,
                      }}
                    >
                      <LogOut size={13} />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </nav>

        {/* ── Right controls ────────────────────────────────────────────── */}
        <div style={{ display:"flex", alignItems:"center", gap:8, flexShrink:0 }}>

          {/* Desktop: Login / user badge */}
          <div style={{ display: isDesktop ? "flex" : "none", alignItems:"center", gap:8 }}>
            {currentUser ? (
              <div style={{
                display:     "flex",
                alignItems:  "center",
                gap:         6,
                padding:     "5px 10px",
                background:  C.bgCard,
                border:      `1px solid ${C.border}`,
                borderRadius: 6,
                fontSize:    11,
                color:       C.text,
              }}>
                <div style={{
                  width:20, height:20, borderRadius:"50%",
                  background: C.green,
                  display:"flex", alignItems:"center", justifyContent:"center",
                }}>
                  <User size={11} color={C.gold} />
                </div>
                <span style={{ maxWidth:80, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" as const }}>
                  {currentUser.name || currentUser.email.split("@")[0]}
                </span>
              </div>
            ) : (
              <button
                onClick={onLoginClick}
                style={{
                  display:     "flex",
                  alignItems:  "center",
                  gap:         6,
                  padding:     "8px 16px",
                  background:  "transparent",
                  border:      `1px solid ${C.gold}`,
                  borderRadius: 6,
                  cursor:      "pointer",
                  color:       C.gold,
                  fontSize:    12,
                  fontWeight:  600,
                  letterSpacing: 0.5,
                  transition:  "all 0.15s",
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLButtonElement).style.background = C.gold;
                  (e.currentTarget as HTMLButtonElement).style.color = "#000";
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLButtonElement).style.background = "transparent";
                  (e.currentTarget as HTMLButtonElement).style.color = C.gold;
                }}
              >
                <LogIn size={13} />
                Staff Login
              </button>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(prev => !prev)}
            style={{
              display:    isDesktop ? "none" : "flex",
              alignItems: "center",
              justifyContent:"center",
              width:      38,
              height:     38,
              background: mobileOpen ? C.bgCard : "none",
              border:     `1px solid ${mobileOpen ? C.border : "transparent"}`,
              borderRadius: 6,
              cursor:     "pointer",
              color:      C.text,
            }}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* ── Mobile overlay menu ───────────────────────────────────────────── */}
      {mobileOpen && (
        <div style={{
          position:   "fixed",
          top:        0,
          left:       0,
          right:      0,
          bottom:     0,
          background: C.bg,
          zIndex:     999,
          overflowY:  "auto" as const,
          display:    "flex",
          flexDirection:"column" as const,
        }}>
          {/* Mobile header */}
          <div style={{
            display:       "flex",
            alignItems:    "center",
            justifyContent:"space-between",
            padding:       "14px 20px",
            borderBottom:  `1px solid ${C.border}`,
            background:    C.bgSurface,
          }}>
            <div style={{ fontFamily:"Cinzel, serif", fontSize:14, color:C.gold, fontWeight:700 }}>
              ERUE Festival 2026
            </div>
            <button
              onClick={() => setMobileOpen(false)}
              style={{
                display:"flex", alignItems:"center", justifyContent:"center",
                width:34, height:34, background:C.bgCard, border:`1px solid ${C.border}`,
                borderRadius:6, cursor:"pointer", color:C.text,
              }}
              aria-label="Close menu"
            >
              <X size={18} />
            </button>
          </div>

          {/* Mobile user / login */}
          <div style={{ padding:"12px 20px", borderBottom:`1px solid ${C.border}` }}>
            {currentUser ? (
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <div style={{
                    width:36, height:36, borderRadius:"50%", background:C.green,
                    display:"flex", alignItems:"center", justifyContent:"center",
                  }}>
                    <User size={18} color={C.gold} />
                  </div>
                  <div>
                    <div style={{ fontSize:13, fontWeight:600, color:C.text }}>
                      {currentUser.name || currentUser.email.split("@")[0]}
                    </div>
                    <div style={{ fontSize:10, color:C.gold, textTransform:"uppercase" as const, letterSpacing:1 }}>
                      {currentUser.systemRole}
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  style={{
                    display:"flex", alignItems:"center", gap:5,
                    padding:"6px 12px", background:"none", border:`1px solid #ef4444`,
                    borderRadius:6, cursor:"pointer", color:"#ef4444", fontSize:12,
                  }}
                >
                  <LogOut size={13} />Sign Out
                </button>
              </div>
            ) : (
              <button
                onClick={() => { setMobileOpen(false); onLoginClick(); }}
                style={{
                  display:"flex", alignItems:"center", justifyContent:"center", gap:8,
                  width:"100%", padding:"12px 16px",
                  background:C.gold, border:"none", borderRadius:8,
                  cursor:"pointer", color:"#000", fontSize:14, fontWeight:700,
                }}
              >
                <LogIn size={16} />
                Staff Login — Authorised Personnel Only
              </button>
            )}
          </div>

          {/* Mobile accordion groups */}
          <div style={{ flex:1, overflowY:"auto" as const, padding:"8px 0" }}>
            {PUBLIC_MENU_GROUPS.map(group => (
              <div key={group.key} style={{ borderBottom:`1px solid ${C.border}` }}>
                <button
                  onClick={() => setMobileGroup(prev => prev === group.key ? null : group.key)}
                  style={{
                    display:       "flex",
                    alignItems:    "center",
                    justifyContent:"space-between",
                    width:         "100%",
                    padding:       "14px 20px",
                    background:    mobileGroup === group.key ? C.bgCard : "none",
                    border:        "none",
                    cursor:        "pointer",
                    color:         C.text,
                    fontSize:      14,
                    fontWeight:    600,
                  }}
                  aria-expanded={mobileGroup === group.key}
                >
                  <span>{group.emoji} {group.label}</span>
                  <ChevronDown
                    size={16}
                    style={{
                      transform:  mobileGroup === group.key ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s",
                      color:      C.textMuted,
                    }}
                  />
                </button>

                {mobileGroup === group.key && (
                  <div style={{ background:C.bgCard, padding:"4px 0 8px" }}>
                    {group.items.map(item => (
                      <button
                        key={item.id}
                        onClick={() => navigate(item.id)}
                        style={{
                          display:    "flex",
                          alignItems: "center",
                          gap:        12,
                          width:      "100%",
                          padding:    "10px 32px",
                          background: activeTab === item.id ? C.green : "none",
                          border:     "none",
                          cursor:     "pointer",
                          textAlign:  "left" as const,
                          color:      activeTab === item.id ? C.goldLight : C.text,
                          fontSize:   13,
                        }}
                      >
                        <span style={{ color: activeTab === item.id ? C.gold : C.textMuted }}>
                          {item.icon}
                        </span>
                        <div>
                          <div style={{ fontWeight:500 }}>{item.label}</div>
                          <div style={{ fontSize:10, color:C.textMuted }}>{item.description}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Mobile staff section */}
            {currentUser && permitted.length > 0 && (
              <div style={{ borderBottom:`1px solid ${C.border}` }}>
                <button
                  onClick={() => setMobileStaff(prev => !prev)}
                  style={{
                    display:       "flex",
                    alignItems:    "center",
                    justifyContent:"space-between",
                    width:         "100%",
                    padding:       "14px 20px",
                    background:    mobileStaff ? "#1a2e1a" : "none",
                    border:        "none",
                    cursor:        "pointer",
                    color:         C.gold,
                    fontSize:      14,
                    fontWeight:    700,
                  }}
                  aria-expanded={mobileStaff}
                >
                  <span><Shield size={15} style={{ display:"inline", marginRight:8 }} />Staff Portal</span>
                  <ChevronDown size={16} style={{ transform: mobileStaff ? "rotate(180deg)":"rotate(0deg)", transition:"transform 0.2s" }} />
                </button>

                {mobileStaff && (
                  <div style={{ background:"#0a130d", padding:"4px 0 8px" }}>
                    {groupOrder.filter(g => grouped[g]?.length).map(groupName => (
                      <div key={groupName}>
                        <div style={{
                          padding:"6px 32px 2px",
                          fontSize:9, fontWeight:700,
                          letterSpacing:1.5,
                          textTransform:"uppercase" as const,
                          color:C.textMuted,
                        }}>
                          {groupName}
                        </div>
                        {grouped[groupName].map(tab => (
                          <button
                            key={tab.id}
                            onClick={() => navigate(tab.id)}
                            style={{
                              display:"flex", alignItems:"center", gap:10,
                              width:"100%", padding:"10px 32px",
                              background: activeTab === tab.id ? C.green : "none",
                              border:"none", cursor:"pointer",
                              textAlign:"left" as const,
                              color: activeTab === tab.id ? C.goldLight : C.text,
                              fontSize:13,
                            }}
                          >
                            <Settings size={13} style={{ color:C.textMuted }} />
                            {tab.label}
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mobile footer */}
          <div style={{
            padding:      "12px 20px",
            borderTop:    `1px solid ${C.border}`,
            background:   C.bgSurface,
            textAlign:    "center" as const,
            fontSize:     10,
            color:        C.textMuted,
            letterSpacing:1,
          }}>
            Uzairue Heritage: Culture, Security, Prosperity
          </div>
        </div>
      )}

      {/* Animation keyframe */}
      <style>{`
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateX(-50%) translateY(-8px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </div>
  );
}
